<template>
  <v-menu
    v-model="isMenuOpen"
    offset-y
    content-class="mt-2"
    offset-overflow
    :close-on-content-click="false"
  >
    <template #activator="{on, attrs, value}">
      <button
        class="chip-dropdown common-chip"
        tabindex="1"
        :class="{small}"
        :title="selectedItemsText"
        v-bind="attrs"
        v-on="on"
      >
        <div v-if="value" class="arrow-menu"></div>

        <z-icon color="var(--black)" size="12">
          $wifi_tethering_rounded
        </z-icon>

        <div>{{ selectedItemsText }}</div>

        <z-icon color="var(--black)" size="12">
          {{ value ? '$expand_less' : '$expand_more' }}
        </z-icon>
      </button>
    </template>

    <z-card width="370">
      <template #content>
        <z-input
          class="mb-4"
          hide-details
          :label="inputLabel || $t('search_items')"
          :value="serverSearch ? search : internalSearch"
          @input="onSearchItems"
        >
          <template #append>
            <z-icon class="mt-1" color="var(--black)" size="19">
              $search_rounded
            </z-icon>
          </template>
        </z-input>

        <template v-if="itemsToRender.length !== 0">
          <v-checkbox
            dense
            hide-details
            class="my-0 py-0"
            color="var(--black)"
            on-icon="$check_box_rounded"
            off-icon="$check_box_outline_blank_rounded"
            indeterminate-icon="$indeterminate_check_box_rounded"
            :label="toggleAllIsSelected ? $t('uncheck_all') : $t('check_all')"
            :input-value="toggleAllIsSelected"
            :indeterminate="
              !toggleAllIsSelected &&
              internalItems.some((i) => i.value === true)
            "
            :value="toggleAllIsSelected"
            @change="toggleAllItems"
          ></v-checkbox>

          <v-divider class="my-2"></v-divider>
        </template>

        <z-scrollbar
          class="d-flex flex-column mb-4"
          style="gap: 12px; max-height: 200px"
        >
          <template v-if="itemsToRender.length">
            <v-checkbox
              v-for="(item, index) in itemsToRender"
              :key="index"
              dense
              hide-details
              class="my-0 py-0"
              color="var(--black)"
              on-icon="$check_box_rounded"
              off-icon="$check_box_outline_blank_rounded"
              :input-value="item.value"
              :value="item.value"
              :label="item.text"
              @change="toggleItem(item)"
            ></v-checkbox>
          </template>

          <div v-else class="empty-text">{{ $t('no_items_found') }}</div>
        </z-scrollbar>

        <v-divider class="mb-4"></v-divider>

        <div class="d-flex justify-end">
          <z-btn
            rounded
            small
            class="mr-4"
            :text="$t('cancel')"
            @click="isMenuOpen = false"
          ></z-btn>

          <z-btn
            rounded
            small
            primary
            :text="$t('add')"
            :disabled="disableConfirmButton"
            @click="onAddItems"
          ></z-btn>
        </div>
      </template>
    </z-card>
  </v-menu>
</template>

<script>
export default {
  name: 'ZSelectableChipDropdown',
  props: {
    /**
     * Define os items que serão exibidos no dropdown
     *
     * @typedef {Object} Item - Item que será exibido no dropdown { text: string, value: boolean, id: any}
     */
    items: {
      type: Array,
      default: () => [],
    },

    /**
     * Quando a prop está ativa, emite o evento de atualização da pesquisa de itens
     */
    serverSearch: {
      type: Boolean,
      default: false,
    },

    /**
     * Define a pesquisa de itens, quando usada a prop `serverSearch`
     */
    search: {
      type: String,
      default: '',
    },

    /**
     * Define o texto do label do input
     */

    inputLabel: {
      type: String,
      default: '',
    },

    /**
     * Define o texto do label do chip
     */

    chipLabel: {
      type: String,
      default: '',
    },

    /**
     * Define se o chip deve ser pequeno
     * */
    small: {
      type: Boolean,
      default: false,
    },

    /**
     * Define o texto do chip quando todos os itens forem selecionados
     */
    allItemsSelectedLabel: {
      type: String,
      default: '',
    },
  },

  i18n: {
    messages: {
      en: {
        search_items: 'Search items',
        all_items: 'All items',
        select_items: 'Select items',
        uncheck_all: 'Uncheck all',
        check_all: 'Check all',
        no_items_found: 'No items found.',
        cancel: 'Cancel',
        add: 'Add',
      },
      pt: {
        search_items: 'Pesquisar itens',
        all_items: 'Todos os itens',
        select_items: 'Selecione os itens',
        uncheck_all: 'Desmarcar todos',
        check_all: 'Marcar todos',
        no_items_found: 'Nenhum item encontrado.',
        cancel: 'Cancelar',
        add: 'Adicionar',
      },
      es: {
        search_items: 'Buscar elementos',
        all_items: 'Todos los elementos',
        select_items: 'Seleccione los elementos',
        uncheck_all: 'Desmarcar todos',
        check_all: 'Marcar todos',
        no_items_found: 'No se encontraron elementos.',
        cancel: 'Cancelar',
        add: 'Agregar',
      },
      fr: {
        search_items: 'Rechercher des articles',
        all_items: 'Tous les articles',
        select_items: 'Sélectionner des articles',
        uncheck_all: 'Tout décocher',
        check_all: 'Tout cocher',
        no_items_found: 'Aucun article trouvé.',
        cancel: 'Annuler',
        add: 'Ajouter',
      },
      tr: {
        search_items: 'Öğeleri Ara',
        all_items: 'Tüm Öğeler',
        select_items: 'Öğeleri Seç',
        uncheck_all: 'Tümünü İşaretlemeyi Kaldır',
        check_all: 'Tümünü İşaretle',
        no_items_found: 'Öğe Bulunamadı.',
        cancel: 'İptal',
        add: 'Ekle',
      },
    },
  },

  data() {
    return {
      isMenuOpen: false,
      internalItems: [],
      internalSearch: '',
    }
  },

  computed: {
    itemsToRender() {
      return this.serverSearch ? this.items : this.filteredItems
    },

    toggleAllIsSelected() {
      if (this.serverSearch) {
        if (!this.items.length) return false
        return this.items.every((i) => i.value === true)
      }

      if (!this.filteredItems.length) return false
      return this.filteredItems.every((i) => i.value === true)
    },

    allItemsSelected() {
      if (!this.items.length) return false

      return this.items.every((i) => i.value === true)
    },

    selectedItems() {
      return this.items.filter((i) => i.value === true)
    },

    selectedItemsText() {
      return this.allItemsSelected
        ? this.allItemsSelectedLabel || this.$t('all_items')
        : this.selectedItems.map((i) => i.text).join(', ') ||
            this.chipLabel ||
            this.$t('select_items')
    },

    disableConfirmButton() {
      if (this.serverSearch) {
        return this.items.length === 0
      }

      return this.internalItems.length === 0 || this.filteredItems.length === 0
    },

    filteredItems() {
      if (!this.internalSearch) return this.internalItems

      return this.internalItems.filter((i) =>
        i.text.toLowerCase().includes(this.internalSearch.toLowerCase())
      )
    },
  },

  watch: {
    items: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.internalItems = newVal
      },
    },

    /**
     * trata a abertura/fechamento do menu e o usuário não confirmou,
     * para que os itens internos sejam atualizados de acordo
     */
    isMenuOpen() {
      this.internalSearch = ''
      this.internalItems = this.items
    },
  },

  methods: {
    onAddItems() {
      this.isMenuOpen = false

      const itemsToEmit = this.serverSearch ? this.items : this.internalItems
      this.$emit('update:items', itemsToEmit)
    },

    toggleAllItems(value = false) {
      if (this.serverSearch) {
        this.$emit('toggle:all')
        return
      }

      if (this.filteredItems.every((i) => i.value == value)) {
        return
      }

      this.internalItems = this.internalItems.map((item) => {
        if (this.filteredItems.includes(item)) {
          return {...item, value: value || false}
        } else {
          return item
        }
      })
    },

    toggleItem(item) {
      if (this.serverSearch) {
        this.$emit('toggle:item', item)
        return
      }

      this.internalItems = this.internalItems.map((i) => {
        if (i.id === item.id) {
          return {...i, value: !item.value}
        }

        return i
      })
    },

    onSearchItems(evt) {
      if (this.serverSearch) {
        this.$emit('update:search', evt)
      } else {
        this.internalSearch = evt
      }
    },
  },
}
</script>

<style scoped lang="scss">
@use './common.scss' as *;

:deep(.v-input--checkbox label) {
  font-size: 13px;
  font-weight: 400;
  color: var(--black);
}

.empty-text {
  margin: 20px 0;
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  line-height: 15px;
  color: var(--black-600);
}

.arrow-menu {
  transform: translateX(-50%);
  position: absolute;
  border-bottom: solid 8px var(--white) !important;
  border-right: solid 8px transparent;
  border-left: solid 8px transparent;
  top: 21px;
  left: 50%;
  height: 0px;
  width: 0px;
  z-index: 9;
}
</style>
